// Send equipment back after an event (Stage 2B), with "Lost or broken"
// (Batch 5): per item how many come back to the store and how many will
// never come back — written off with a manager PIN, so they stop showing as
// still out. Rules: lib/inventoryFlow/equipmentLogic.ts.
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { intentScope, useIntentKey } from '@/lib/inventoryFlow/intentKey'
import { fmtAmount } from '@/lib/inventoryFlow/planLogic'
import { EQ } from '@/lib/inventoryFlow/equipmentStrings'
import {
  equipmentProblem, equipmentReturnBody, equipmentSubmitLabel, initialEquipmentDrafts, needsWriteOffPin, toSettle, type EquipmentDraft, type WriteOffKind,
} from '@/lib/inventoryFlow/equipmentLogic'
import type { PlanReturnable } from '@/lib/inventoryFlow/planTypes'
import { QtyStepper } from './QtyStepper'

type Notify = (kind: 'success' | 'error', message: string) => void

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message.split(' — ')[0] : 'Something went wrong'
}

export function EquipmentReturnPanel({ planId, returnables, notify, onClose }: {
  planId: string
  returnables: PlanReturnable[]
  notify: Notify
  onClose: () => void
}) {
  const qc = useQueryClient()
  const rows = returnables.filter((r) => toSettle(r) > 1e-6)
  const [drafts, setDrafts] = useState<Record<string, EquipmentDraft>>(() => initialEquipmentDrafts(rows))
  const [pin, setPin] = useState('')
  const body = equipmentReturnBody(drafts, pin)
  // One key per send as typed: the same amounts retried reuse it.
  const intent = useIntentKey(intentScope('return-equipment', planId, body.lines, 'write_offs' in body ? body.write_offs : []))
  const save = useMutation({
    mutationFn: () => apiClient.productionPlanAction(planId, 'return-equipment', body, intent.key),
    onSuccess: () => {
      intent.renew()
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('success', EQ.done)
      onClose()
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('error', errMessage(e))
    },
  })
  const set = (id: string, patch: Partial<EquipmentDraft>) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }))
  const problem = equipmentProblem(rows, drafts, pin)

  return (
    <section className="space-y-3 rounded-lg border-2 border-amber-300 p-4 dark:border-amber-800">
      <div>
        <h3 className="text-base font-semibold">{EQ.returnTitle}</h3>
        <p className="text-sm text-muted-foreground">{EQ.returnHelp}</p>
      </div>
      <ul className="space-y-3">
        {rows.map((r) => {
          const d = drafts[r.stock_item_id]
          if (!d) return null
          return (
            <li key={r.stock_item_id} className="space-y-2 rounded-lg border p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">{r.name}</span>
                <span className="text-sm text-muted-foreground">
                  {EQ.stillOut(`${fmtAmount(r.outstanding)} ${r.unit}`)}
                  {(r.on_way_back ?? 0) > 1e-6 && ` · ${EQ.onWayBack(`${fmtAmount(r.on_way_back ?? 0)} ${r.unit}`)}`}
                  {(r.missing_on_way_back ?? 0) > 1e-6 && ` · ${EQ.missingOnWayBack(`${fmtAmount(r.missing_on_way_back ?? 0)} ${r.unit}`)}`}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-sm font-medium">{EQ.comingBack}</span>
                  <QtyStepper value={d.back} unit={r.unit} label={`${EQ.comingBack} ${r.name}`} min={0} onChange={(v) => set(r.stock_item_id, { back: v })} />
                </label>
                <label className="space-y-1">
                  <span className="text-sm font-medium">{EQ.lostOrBroken}</span>
                  <QtyStepper value={d.lost} unit={r.unit} label={`${EQ.lostOrBroken} ${r.name}`} min={0} onChange={(v) => set(r.stock_item_id, { lost: v })} />
                </label>
              </div>
              {d.lost > 1e-6 && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex w-fit gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={`${EQ.lostOrBroken} ${r.name}`}>
                    {(Object.keys(EQ.kinds) as WriteOffKind[]).map((k) => (
                      <button key={k} type="button" role="radio" aria-checked={d.kind === k} onClick={() => set(r.stock_item_id, { kind: k })}
                        className={cn('rounded-md px-4 py-2 text-sm font-medium transition-colors', d.kind === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                        {EQ.kinds[k]}
                      </button>
                    ))}
                  </div>
                  <Input className="min-w-[12rem] flex-1" maxLength={300} placeholder={EQ.whatHappened} aria-label={`${EQ.whatHappened} ${r.name}`}
                    value={d.note} onChange={(e) => set(r.stock_item_id, { note: e.target.value })} />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {needsWriteOffPin(drafts) && (
        <label className="block max-w-xs space-y-1">
          <span className="text-sm font-medium">{EQ.pin}</span>
          <Input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
        </label>
      )}
      {problem === 'tooMuch' && <p className="text-sm text-destructive">{EQ.tooMuch}</p>}
      {problem === 'nothing' && <p className="text-sm text-muted-foreground">{EQ.nothing}</p>}
      <div className="flex flex-wrap gap-2">
        <Button disabled={problem != null || save.isPending} onClick={() => save.mutate()}>{save.isPending ? EQ.saving : equipmentSubmitLabel(drafts)}</Button>
        <Button variant="ghost" onClick={onClose}>{EQ.cancel}</Button>
      </div>
    </section>
  )
}
